import { Group, Badge } from '@mantine/core';
import type { TaskType, TaskCompletion } from '@/types/task';
import { TASK_TYPE_LABELS, TASK_TYPE_COLORS } from '@/types/task';

interface TaskTypeFilterProps {
  completions: TaskCompletion[];
  selectedTypes: TaskType[];
  onSelectionChange: (types: TaskType[]) => void;
}

export function TaskTypeFilter({
  completions,
  selectedTypes,
  onSelectionChange,
}: TaskTypeFilterProps) {
  // Calculate counts per type
  const typeCounts = completions.reduce((acc, completion) => {
    if (completion.task_type) {
      acc[completion.task_type] = (acc[completion.task_type] || 0) + 1;
    }
    return acc;
  }, {} as Record<TaskType, number>);

  const availableTypes = Object.keys(typeCounts) as TaskType[];

  const handleToggle = (type: TaskType) => {
    if (selectedTypes.includes(type)) {
      onSelectionChange(selectedTypes.filter((t) => t !== type));
    } else {
      onSelectionChange([...selectedTypes, type]);
    }
  };

  if (availableTypes.length === 0) {
    return null;
  }

  return (
    <Group gap="xs">
      {availableTypes.map((type) => {
        const isSelected = selectedTypes.includes(type);
        const count = typeCounts[type] || 0;

        return (
          <Badge
            key={type}
            color={TASK_TYPE_COLORS[type]}
            variant={isSelected ? 'light' : 'outline'}
            style={{
              cursor: 'pointer',
              opacity: isSelected ? 1 : 0.6,
            }}
            onClick={() => handleToggle(type)}
          >
            {TASK_TYPE_LABELS[type]} ({count})
          </Badge>
        );
      })}
    </Group>
  );
}
